import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Star, MessageSquare, Check, X, Send, BarChart3, Download, Search } from 'lucide-react';
import api from '@/lib/api';
import { formatDate, cn } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Modal from '@/components/ui/Modal';
import Textarea from '@/components/ui/Textarea';
import Pagination from '@/components/ui/Pagination';

type ReviewStatus = 'pending' | 'approved' | 'rejected';

interface ReviewRaw {
  id: string;
  product_name: string;
  first_name: string | null;
  last_name: string | null;
  user_email: string;
  rating: number;
  title: string | null;
  body: string;
  is_approved: boolean;
  created_at: string;
  admin_response: string | null;
}

interface Review extends Omit<ReviewRaw, 'is_approved' | 'first_name' | 'last_name' | 'user_email'> {
  customer_name: string;
  status: ReviewStatus;
}

interface ReviewsResponse {
  reviews: ReviewRaw[];
  total: number;
  page: number;
  limit: number;
}

function mapReviews(raw: ReviewRaw[], status: ReviewStatus): Review[] {
  return raw.map((r) => ({
    ...r,
    customer_name:
      r.first_name && r.last_name ? `${r.first_name} ${r.last_name}` : r.user_email,
    status: (status === 'pending' ? 'pending' : r.is_approved ? 'approved' : 'rejected') as ReviewStatus,
  }));
}

export default function Reviews() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentStatus = (searchParams.get('status') || 'pending') as ReviewStatus;
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentSearch = searchParams.get('search') || '';

  const [searchInput, setSearchInput] = useState(currentSearch);
  const [selectedReviews, setSelectedReviews] = useState<Set<string>>(new Set());
  const [expandedReviews, setExpandedReviews] = useState<Set<string>>(new Set());

  const [respondModal, setRespondModal] = useState<{ isOpen: boolean; reviewId: string | null }>({
    isOpen: false,
    reviewId: null,
  });
  const [adminResponse, setAdminResponse] = useState('');

  const queryClient = useQueryClient();

  const queryKey = ['admin-reviews', currentStatus, currentPage, currentSearch];

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => {
      const params = new URLSearchParams({
        status: currentStatus,
        page: String(currentPage),
      });
      if (currentSearch) params.set('search', currentSearch);
      return api.get<ReviewsResponse>(`/admin/reviews?${params.toString()}`);
    },
  });

  const reviews: Review[] = data ? mapReviews(data.reviews, currentStatus) : [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  const currentPageNum = data?.page ?? currentPage;

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['admin-reviews'] });
    setSelectedReviews(new Set());
  }

  const approveMutation = useMutation({
    mutationFn: (reviewId: string) => api.put(`/admin/reviews/${reviewId}/approve`, {}),
    onSuccess: invalidate,
  });

  const rejectMutation = useMutation({
    mutationFn: (reviewId: string) => api.put(`/admin/reviews/${reviewId}/reject`, {}),
    onSuccess: invalidate,
  });

  const bulkApproveMutation = useMutation({
    mutationFn: (ids: string[]) => api.post('/admin/reviews/bulk-approve', { ids }),
    onSuccess: invalidate,
  });

  const respondMutation = useMutation({
    mutationFn: ({ reviewId, response }: { reviewId: string; response: string }) =>
      api.put(`/admin/reviews/${reviewId}/respond`, { admin_response: response }),
    onSuccess: () => {
      invalidate();
      closeRespondModal();
    },
  });

  const isProcessing =
    approveMutation.isPending ||
    rejectMutation.isPending ||
    bulkApproveMutation.isPending;

  function changeTab(status: ReviewStatus) {
    setSelectedReviews(new Set());
    setSearchParams({ status, page: '1' });
    setSearchInput('');
  }

  function handlePageChange(page: number) {
    const next: Record<string, string> = { status: currentStatus, page: page.toString() };
    if (currentSearch) next.search = currentSearch;
    setSearchParams(next);
  }

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = { status: currentStatus, page: '1' };
    if (searchInput.trim()) next.search = searchInput.trim();
    setSearchParams(next);
    setSelectedReviews(new Set());
  }

  function handleSearchClear() {
    setSearchInput('');
    setSearchParams({ status: currentStatus, page: '1' });
    setSelectedReviews(new Set());
  }

  function toggleReviewSelection(reviewId: string) {
    const newSelected = new Set(selectedReviews);
    if (newSelected.has(reviewId)) {
      newSelected.delete(reviewId);
    } else {
      newSelected.add(reviewId);
    }
    setSelectedReviews(newSelected);
  }

  function toggleSelectAll() {
    if (selectedReviews.size === reviews.length) {
      setSelectedReviews(new Set());
    } else {
      setSelectedReviews(new Set(reviews.map((r) => r.id)));
    }
  }

  function toggleExpandReview(reviewId: string) {
    const next = new Set(expandedReviews);
    if (next.has(reviewId)) {
      next.delete(reviewId);
    } else {
      next.add(reviewId);
    }
    setExpandedReviews(next);
  }

  function openRespondModal(reviewId: string) {
    const review = reviews.find((r) => r.id === reviewId);
    setAdminResponse(review?.admin_response || '');
    setRespondModal({ isOpen: true, reviewId });
  }

  function closeRespondModal() {
    setRespondModal({ isOpen: false, reviewId: null });
    setAdminResponse('');
  }

  function handleSubmitResponse() {
    if (!respondModal.reviewId || !adminResponse.trim()) return;
    respondMutation.mutate({ reviewId: respondModal.reviewId, response: adminResponse });
  }

  function renderStars(rating: number) {
    return (
      <div className="flex gap-1" role="img" aria-label={`${rating} out of 5 stars`}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            className={cn('w-5 h-5', i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300')}
            aria-hidden="true"
          />
        ))}
      </div>
    );
  }

  function getStatusBadgeVariant(status: ReviewStatus) {
    switch (status) {
      case 'approved':
        return 'green';
      case 'rejected':
        return 'red';
      case 'pending':
        return 'yellow';
      default:
        return 'gray';
    }
  }

  const exportUrl = `${import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api'}/admin/exports/reviews/export`;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading reviews...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-8 h-8 text-gray-700" aria-hidden="true" />
          <h1 className="text-3xl font-bold text-gray-900">Reviews</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/reviews/analytics">
            <Button variant="outline" size="sm">
              <BarChart3 className="w-4 h-4 mr-1.5" aria-hidden="true" />
              Analytics
            </Button>
          </Link>
          <a href={exportUrl} download>
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-1.5" aria-hidden="true" />
              Export CSV
            </Button>
          </a>
        </div>
      </div>

      <div className="flex items-center justify-between mb-6">
        <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search product, customer, or review..."
              className="pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-72"
              aria-label="Search reviews"
            />
          </div>
          <Button type="submit" variant="outline" size="sm">
            Search
          </Button>
          {currentSearch && (
            <Button type="button" variant="outline" size="sm" onClick={handleSearchClear}>
              Clear
            </Button>
          )}
        </form>
        {currentStatus === 'pending' && selectedReviews.size > 0 && (
          <Button
            variant="primary"
            onClick={() => bulkApproveMutation.mutate(Array.from(selectedReviews))}
            isLoading={bulkApproveMutation.isPending}
            aria-label={`Approve ${selectedReviews.size} selected reviews`}
          >
            <Check className="w-4 h-4 mr-2" aria-hidden="true" />
            Approve Selected ({selectedReviews.size})
          </Button>
        )}
      </div>

      {isError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">Failed to load reviews. Please try again.</p>
        </div>
      )}

      {(approveMutation.isError || rejectMutation.isError || bulkApproveMutation.isError || respondMutation.isError) && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">Action failed. Please try again.</p>
        </div>
      )}

      <div className="mb-6 border-b border-gray-200" role="tablist" aria-label="Review status tabs">
        <nav className="flex gap-8">
          {(['pending', 'approved', 'rejected'] as ReviewStatus[]).map((status) => (
            <button
              key={status}
              role="tab"
              aria-selected={currentStatus === status}
              aria-controls={`${status}-panel`}
              onClick={() => changeTab(status)}
              className={cn(
                'pb-4 px-1 font-medium text-sm border-b-2 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                currentStatus === status
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="w-full" role="table" aria-label="Reviews list">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {currentStatus === 'pending' && (
                  <th scope="col" className="px-6 py-3 w-12">
                    <input
                      type="checkbox"
                      checked={selectedReviews.size === reviews.length && reviews.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      aria-label="Select all reviews"
                    />
                  </th>
                )}
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rating
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Review
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reviews.length === 0 ? (
                <tr>
                  <td colSpan={currentStatus === 'pending' ? 8 : 7} className="px-6 py-12 text-center text-gray-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-400" aria-hidden="true" />
                    <p className="text-lg">
                      {currentSearch ? `No ${currentStatus} reviews matching "${currentSearch}"` : `No ${currentStatus} reviews`}
                    </p>
                  </td>
                </tr>
              ) : (
                reviews.map((review) => {
                  const isExpanded = expandedReviews.has(review.id);
                  return (
                    <tr key={review.id} className="hover:bg-gray-50 transition-colors">
                      {currentStatus === 'pending' && (
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedReviews.has(review.id)}
                            onChange={() => toggleReviewSelection(review.id)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            aria-label={`Select review for ${review.product_name}`}
                          />
                        </td>
                      )}
                      <td className="px-6 py-4 font-medium text-gray-900">
                        {review.product_name}
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {review.customer_name}
                      </td>
                      <td className="px-6 py-4">
                        {renderStars(review.rating)}
                      </td>
                      <td className="px-6 py-4 max-w-md">
                        {review.title && <p className="font-semibold text-gray-900 mb-1">{review.title}</p>}
                        <p
                          className={cn(
                            'text-gray-600 text-sm cursor-pointer select-none',
                            !isExpanded && 'line-clamp-2'
                          )}
                          onClick={() => toggleExpandReview(review.id)}
                          title={isExpanded ? 'Click to collapse' : 'Click to expand'}
                        >
                          {review.body}
                        </p>
                        {review.admin_response && (
                          <div className="mt-2 p-2 bg-blue-50 rounded border-l-2 border-blue-400">
                            <p className="text-xs font-medium text-blue-900 mb-1">Your Response:</p>
                            <p className="text-sm text-blue-800">{review.admin_response}</p>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-gray-600 text-sm">
                        {formatDate(new Date(review.created_at))}
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant={getStatusBadgeVariant(review.status)}>
                          {review.status.charAt(0).toUpperCase() + review.status.slice(1)}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        {currentStatus === 'pending' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => approveMutation.mutate(review.id)}
                              disabled={isProcessing}
                              aria-label={`Approve review for ${review.product_name}`}
                            >
                              <Check className="w-4 h-4" aria-hidden="true" />
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => rejectMutation.mutate(review.id)}
                              disabled={isProcessing}
                              aria-label={`Reject review for ${review.product_name}`}
                            >
                              <X className="w-4 h-4" aria-hidden="true" />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openRespondModal(review.id)}
                          aria-label={`Respond to review for ${review.product_name}`}
                        >
                          <MessageSquare className="w-4 h-4" aria-hidden="true" />
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {totalPages > 1 && (
        <div className="mt-6 flex justify-center">
          <Pagination
            currentPage={currentPageNum}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      <Modal isOpen={respondModal.isOpen} onClose={closeRespondModal} title="Respond to Review">
        <div className="space-y-4">
          <Textarea
            label="Your Response"
            value={adminResponse}
            onChange={(e) => setAdminResponse(e.target.value)}
            placeholder="Write your response to the customer..."
            rows={4}
            helperText="This response will be visible to customers on the product page"
          />
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={closeRespondModal} disabled={respondMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitResponse}
              isLoading={respondMutation.isPending}
              disabled={!adminResponse.trim()}
            >
              <Send className="w-4 h-4 mr-2" aria-hidden="true" />
              Submit Response
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
